import { TextField, Select, MenuItem, Switch, FormControlLabel, Box, Typography, Grid, Alert, Checkbox } from '@mui/material';
import FieldWrapper from '../FieldWrapper';
import FileUpload from '../FileUpload';
import ResidenzaDocumentUpload from '../ResidenzaDocumentUpload';

const TIPI_DOCUMENTO = ["Carta d'Identità", 'Patente', 'Passaporto', 'Tessera Ministeriale'];
const ENTI_RILASCIO = ['Comune', 'Prefettura / Motorizzazione', 'Questura', 'Ministero'];

export default function Step6Documento({ data, onChange, errors, metadata }) {
  const meta = (f) => metadata?.[f] || {};

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Documento d'identità
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Inserisci i dati del tuo documento
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FieldWrapper label="Tipo Documento" required error={errors?.tipo_documento} completed={!!data.tipo_documento}>
            <Select 
              fullWidth 
              size="small" 
              value={data.tipo_documento || ''} 
              onChange={e => onChange('tipo_documento', e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>Seleziona tipo</MenuItem>
              {TIPI_DOCUMENTO.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FieldWrapper>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FieldWrapper label="Numero Documento" required error={errors?.numero_documento} completed={!!data.numero_documento} locked={meta('numero_documento').editable === false} source={meta('numero_documento').source}>
            <TextField fullWidth size="small" value={data.numero_documento || ''} onChange={e => onChange('numero_documento', e.target.value)} placeholder="N° documento" />
          </FieldWrapper>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FieldWrapper label="Data Rilascio" required error={errors?.data_rilascio} completed={!!data.data_rilascio}>
            <TextField fullWidth size="small" type="date" InputLabelProps={{ shrink: true }} value={data.data_rilascio || ''} onChange={e => onChange('data_rilascio', e.target.value)} />
          </FieldWrapper>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FieldWrapper label="Località Rilascio" error={errors?.localita_rilascio} completed={!!data.localita_rilascio}>
            <TextField fullWidth size="small" value={data.localita_rilascio || ''} onChange={e => onChange('localita_rilascio', e.target.value)} placeholder="Comune di rilascio" />
          </FieldWrapper>
        </Grid>

        <Grid item xs={12}>
          <FieldWrapper label="Ente Rilascio" required error={errors?.ente_rilascio} completed={!!data.ente_rilascio}>
            <Select 
              fullWidth 
              size="small" 
              value={data.ente_rilascio || ''} 
              onChange={e => onChange('ente_rilascio', e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>Seleziona ente</MenuItem>
              {ENTI_RILASCIO.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FieldWrapper>
        </Grid>
      </Grid>

      {data.tipo_documento === "Carta d'Identità" && (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Se il documento d'identità è la <strong>Carta d'Identità Elettronica</strong> (tesserina plastificata), non è necessario allegare la tessera sanitaria.
        </Alert>
      )}

      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 1 }}>
          Scansione documento
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FileUpload label="Fronte" value={data.documento_fronte_url} onChange={v => onChange('documento_fronte_url', v)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FileUpload label="Retro" value={data.documento_retro_url} onChange={v => onChange('documento_retro_url', v)} />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 3 }}>
          <FormControlLabel
            control={
              <Switch 
                checked={data.residenza_non_aggiornata || false} 
                onChange={e => onChange('residenza_non_aggiornata', e.target.checked)} 
              />
            }
            label={<Typography variant="body2">La residenza sul documento non è aggiornata o non coincide con quella dichiarata</Typography>}
            sx={{ m: 0, cursor: 'pointer' }}
          />
        </Box>

        {data.residenza_non_aggiornata && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="warning" sx={{ borderRadius: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>La residenza sul documento non risulta aggiornata.</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                In questo caso è necessario caricare un documento che provi il nuovo indirizzo di residenza. <strong>L'autocertificazione NON è valida.</strong>
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>Puoi inviare uno dei seguenti documenti:</Typography>
              <Box component="ol" sx={{ pl: 2, mt: 1, '& li': { mb: 0.5 } }}>
                <li>Libretto veicolo da cui si evince la residenza</li>
                <li>Busta paga con indirizzo cliente / cedolino pensione / OBIS M</li>
                <li>Certificato di residenza o stato civile con indirizzo di residenza, non più vecchio di 90 giorni (gratuito tramite SPID)</li>
                <li>Tessera elettorale</li>
                <li>Bolletta utenza domestica (acqua, luce, gas), non più vecchia di 90 giorni, intestata al cliente</li>
              </Box>
            </Alert>
            <ResidenzaDocumentUpload data={data} onChange={onChange} />

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 2, bgcolor: 'action.hover', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Checkbox
                checked={data.presa_visione_residenza || false}
                onChange={e => onChange('presa_visione_residenza', e.target.checked)}
                size="small"
                sx={{ p: 0, mt: 0.25 }}
              />
              <Typography variant="body2" color="text.primary">
                Ho preso visione delle istruzioni e provvederò a caricare un documento valido che attesti la residenza aggiornata.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}